import { type FormEvent, useEffect, useState } from 'react'
import { ArrowRight, CheckCircle2, Eye, EyeOff, LockKeyhole, ShieldCheck, Sparkles, UserRound } from 'lucide-react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Brand } from '@/components/Brand'
import { Button, Input } from '@/components/ui'
import { useAuth } from '@/contexts/AuthContext'
import { fetchPublicSettings } from '@/services/appService'
import type { SystemSettings } from '@/types'
import { safeErrorMessage } from '@/utils'

type LoginLocationState = { from?: { pathname?: string }; message?: string }

export function LoginPage() {
  const { profile, loading: authLoading, demoMode, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LoginLocationState | null
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [branding, setBranding] = useState<Pick<SystemSettings, 'companyName' | 'systemName' | 'welcomeText' | 'logoUrl'>>({
    companyName: 'Viandas para equipos',
    systemName: 'Buen Plato',
    welcomeText: 'Elegí cada vianda en segundos. Sin planillas, mensajes perdidos ni pedidos a último momento.',
    logoUrl: null,
  })

  useEffect(() => {
    if (state?.message) setError(state.message)
  }, [state?.message])

  useEffect(() => {
    let active = true
    void fetchPublicSettings()
      .then((settings) => active && setBranding(settings))
      .catch((caught) => console.warn('No se pudo cargar el branding público.', caught))
    return () => { active = false }
  }, [])

  if (!authLoading && profile) return <Navigate to={profile.role === 'admin' ? '/admin' : '/dashboard'} replace />

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (!identifier.trim() || !password) {
      setError('Ingresá tu usuario y contraseña o PIN.')
      return
    }
    setSubmitting(true)
    try {
      const nextProfile = await signIn({ identifier, password })
      const requested = state?.from?.pathname
      const isAllowed = requested && (nextProfile.role === 'admin' ? requested.startsWith('/admin') : !requested.startsWith('/admin'))
      navigate(isAllowed ? requested : nextProfile.role === 'admin' ? '/admin' : '/dashboard', { replace: true })
    } catch (caught) {
      setError(safeErrorMessage(caught, 'No pudimos iniciar sesión. Intentá nuevamente.'))
    } finally {
      setSubmitting(false)
    }
  }

  const fillDemoAccount = (role: 'admin' | 'employee') => {
    setIdentifier(role === 'admin' ? 'admin@empresa.com' : 'juan.perez')
    setPassword(role === 'admin' ? 'demo123' : '1234')
    setError('')
  }

  return (
    <main className="min-h-dvh bg-cream lg:grid lg:grid-cols-[minmax(0,1.08fr)_minmax(440px,.92fr)]">
      <section className="relative hidden min-h-dvh overflow-hidden bg-forest-900 p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14">
        <div className="absolute -right-28 -top-32 h-[32rem] w-[32rem] rounded-full bg-forest-600/35 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-[34rem] w-[34rem] rounded-full bg-saffron-400/10 blur-3xl" />
        <div className="relative"><Brand inverse name={branding.systemName} subtitle={branding.companyName} logoUrl={branding.logoUrl} /></div>
        <div className="relative max-w-2xl py-12">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-sm font-semibold text-forest-100 backdrop-blur">
            <Sparkles className="h-4 w-4 text-saffron-300" /> Simple para todos
          </span>
          <h1 className="font-display text-5xl font-extrabold leading-[1.08] tracking-[-.04em] xl:text-6xl">Tu período,<br /><span className="text-saffron-300">bien servido.</span></h1>
          <p className="mt-6 max-w-xl text-lg leading-8 text-forest-100/85">{branding.welcomeText}</p>
          <div className="mt-10 grid max-w-xl gap-4 sm:grid-cols-2">
            {['Elegí todo el período de forma simple', 'Tus cambios se guardan automáticamente', 'Consultá tu historial cuando quieras', 'Información protegida por perfil'].map((feature) => (
              <div key={feature} className="flex items-start gap-3 text-sm leading-6 text-forest-50"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-saffron-300" />{feature}</div>
            ))}
          </div>
        </div>
        <p className="relative text-sm text-forest-300">Gestión clara para personas y equipos.</p>
      </section>

      <section className="flex min-h-dvh items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-md animate-fade-up">
          <Brand className="mb-10 lg:hidden" name={branding.systemName} subtitle={branding.companyName} logoUrl={branding.logoUrl} />
          <div className="mb-8">
            <p className="eyebrow">Bienvenido de nuevo</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Iniciá sesión</h2>
            <p className="mt-3 text-[15px] leading-6 text-slate-500">Ingresá con el usuario que te proporcionó la empresa.</p>
          </div>

          {error && <div className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert"><LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span></div>}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <label htmlFor="identifier" className="mb-2 block text-sm font-bold text-slate-700">Usuario o correo electrónico</label>
              <div className="relative">
                <UserRound className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                <Input id="identifier" value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" autoCapitalize="none" placeholder="ej. juan.perez" className="h-12 pl-11" disabled={submitting} autoFocus />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-bold text-slate-700">Contraseña o PIN</label>
              <div className="relative">
                <LockKeyhole className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" placeholder="Ingresá tu clave" className="h-12 px-11" disabled={submitting} />
                <button type="button" onClick={() => setShowPassword((visible) => !visible)} className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-forest-500" aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>
            <Button type="submit" size="lg" className="w-full" loading={submitting || authLoading}>Ingresar <ArrowRight className="h-4 w-4" /></Button>
          </form>

          {demoMode && (
            <div className="mt-7 rounded-2xl border border-saffron-200 bg-saffron-50/70 p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-saffron-900"><Sparkles className="h-4 w-4" />Modo demo</div>
              <p className="mt-1.5 text-xs leading-5 text-saffron-800/80">Probá cada experiencia con datos locales. Tus cambios quedarán guardados en este navegador.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => fillDemoAccount('employee')} className="rounded-xl border border-saffron-200 bg-white px-3 py-2.5 text-left text-xs font-bold text-slate-700 transition hover:border-saffron-400 hover:shadow-sm">Empleado<span className="mt-0.5 block font-normal text-slate-500">juan.perez · 1234</span></button>
                <button type="button" onClick={() => fillDemoAccount('admin')} className="rounded-xl border border-saffron-200 bg-white px-3 py-2.5 text-left text-xs font-bold text-slate-700 transition hover:border-saffron-400 hover:shadow-sm">Administrador<span className="mt-0.5 block font-normal text-slate-500">admin · demo123</span></button>
              </div>
            </div>
          )}
          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-4 w-4" />Acceso seguro · No hay registro público</div>
        </div>
      </section>
    </main>
  )
}
